<template>
  <v-tooltip
    :text="t('generic.table.tooltip.displayedColumns')"
    location="left"
  >
    <template #activator="{ props: tooltipProps }">
      <v-menu
        v-model="showMenu"
        :closeOnContentClick="false"
        location="left"
        :offset="[6, 0]"
        contentClass="show-columns-menu"
      >
        <template #activator="{ props: menuProps }">
          <v-icon-btn
            v-bind="mergeProps(tooltipProps, menuProps)"
            icon="huge:layout-3-column"
            color="primary"
            variant="tonal"
            :data-cc="`customizer-btn-${tableId}`"
          />
        </template>

        <v-card class="d-flex flex-column pa-4">
          <span class="text-h6">
            {{ $t('statistics.liveStatus.visibleColumns') }}
          </span>

          <v-divider class="mt-2 mb-1" />

          <v-checkbox
            v-for="header in availableHeaders"
            v-model="showColumns"
            :key="header.key"
            :value="header.key"
            :label="header.title"
            :disabled="!!header.default"
          />
        </v-card>
      </v-menu>
    </template>
  </v-tooltip>
</template>

<script setup lang="ts">
import { ref, computed, watch, mergeProps } from 'vue';
import { useStorage } from '@vueuse/core';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps<{
  headers: any[];
  tableId: string;
}>();

const emit = defineEmits(['setColumns']);

const showMenu = ref(false);
const storageKey = `displayColumns.${props.tableId}`;

const visibleHeaders = computed(() =>
  props.headers.filter((header) => !header.align?.includes('d-none'))
);

const defaultColumnsObject = computed(() =>
  visibleHeaders.value.reduce((acc: Record<string, boolean>, h) => {
    acc[h.key] = true;
    return acc;
  }, {})
);

const storedColumns = useStorage<Record<string, boolean>>(
  storageKey,
  defaultColumnsObject.value,
  localStorage,
  { mergeDefaults: true }
);

// If there is a new column added to the table, it will be added to the localStorage and shown
const showColumns = computed({
  get: () => {
    // Convert Object { a: true, b: false } -> Array ['a']
    return Object.keys(storedColumns.value).filter(
      (key) => storedColumns.value[key]
    );
  },
  set: (newArray: string[]) => {
    // Convert Array ['a', 'b'] -> Object { a: true, b: true, c: false }
    storedColumns.value = visibleHeaders.value.reduce(
      (acc, { key }) => ({
        ...acc,
        [key]: newArray.includes(key)
      }),
      {} as Record<string, boolean>
    );
  }
});

const availableHeaders = computed(() => {
  return [...visibleHeaders.value]
    .filter((header) => header.title)
    .sort((a, b) => (b.default ? 1 : 0) - (a.default ? 1 : 0));
});

watch(
  showColumns,
  (columns) => {
    emit('setColumns', columns);
  },
  { immediate: true }
);
</script>

<style scoped lang="scss">
:deep(.v-checkbox .v-label) {
  margin-left: var(--v-spacing-1);
}
</style>
